// Usuarios y permisos. Diego: "Todos ven todo. Sin aprobación previa." Solo el director toca precios, usuarios y llaves.
import { z } from 'zod';
import { Correo, FechaHora, Id, Texto, WhatsApp } from './basicos.ts';
import { COMISION_POR_ROL, OPTIMUS } from '../constantes.ts';

export const ROLES = ['director', 'asesor', 'externo', 'tecnico'] as const;
export const Rol = z.enum(ROLES);
export type Rol = z.infer<typeof Rol>;
/** Roles que se pueden dar de alta HOY. ADENDA A0b (10-oct): "Técnicos (Eduardo, Víctor) NO entran al cotizador: el
 *  VENDEDOR captura los costos adicionales que le pasa el técnico". "tecnico" se queda en Rol solo para que los
 *  usuarios viejos sigan validando; no entra, no tiene permisos y no se puede dar de alta. */
export const ROLES_ACTIVOS = ['director', 'asesor', 'externo'] as const satisfies readonly Rol[];

export const Usuario = z.strictObject({
  id: Id,
  correo: Correo,
  nombre: Texto(2, 80),
  rol: Rol,
  activo: z.boolean(),
  /** id del asesor en la plantilla de PDF (Propuesta v2/plantilla/assets/asesores/asesores.json: "miguel", "juan-carlos").
   *  null = no tiene tarjeta de asesor: el PDF de sus cotizaciones TRUENA (N2) hasta que Diego lo dé de alta allá. */
  asesor_id_plantilla: z.string().regex(/^[a-z0-9-]+$/).nullable(),
  whatsapp: WhatsApp.nullable(),
  /** COSTOS-DIEGO (10-oct): comisión de ESTE vendedor (% del contado sin IVA) que se resta en la rentabilidad.
   *  Ausente/null = la de su rol (COMISION_POR_ROL: externo 5, asesor 6, director 0). Iván Arredondo: 10–12 %. Diego la
   *  edita en Configuración → Usuarios. */
  comision_pct: z.number().min(0).max(50).nullable().optional(),
  /** OPTIMUS (Diego, 12-oct noche, ADENDA c): vendedor del aliado comercial Optimus. Sus cotizaciones NUEVAS salen con el
   *  precio de Optimus (tabla × (1 − 6 %) ÷ (1 − comisión total)), la comisión de la cotización es la suya (10 % por
   *  omisión) + 5 % de Juan Carlos, y su tarjeta del PDF lleva la leyenda de Optimus. Ausente = false. Solo Diego la cambia
   *  (Configuración → Usuarios; queda en la bitácora); nadie más la ve (vista.ts). Un director no puede ser de Optimus. */
  optimus: z.boolean().optional(),
  creado_en: FechaHora,
  creado_por: Id.nullable(),
});
export type Usuario = z.infer<typeof Usuario>;

/** Diego, 13-oct (19:05, punto o): vendedor sin tarjeta en la plantilla y sin WhatsApp: el PDF no sale (no se imprime otro
 *  número) y el mensaje dice exactamente qué hacer. El mismo texto en el aviso, en el error del PDF y al crear. */
export function mensajeFaltaWhatsapp(nombre: string): string {
  return `Falta el WhatsApp de ${nombre}: Diego lo pone en Configuración → Usuarios.`;
}

/** Comisión que vale para este vendedor: la suya o, si no tiene, la de su rol (COSTOS-DIEGO, 10-oct); un vendedor de
 *  Optimus, 10 % (Diego, 12-oct noche). Es SOLO la del vendedor: la de Juan Carlos por Optimus va aparte
 *  (comisionAliadoDe). */
export function comisionDe(u: Pick<Usuario, 'rol'> & { comision_pct?: number | null | undefined; optimus?: boolean | undefined }): number {
  return u.comision_pct ?? (u.optimus ? OPTIMUS.comision_vendedor_por_omision_pct : COMISION_POR_ROL[u.rol]);
}

/** Lo que cobra el aliado comercial por una venta de este vendedor (Optimus: 5 % de Juan Carlos Cortés), aparte del vendedor. */
export function comisionAliadoDe(u: { optimus?: boolean | undefined }): number {
  return u.optimus ? OPTIMUS.comision_aliado_pct : 0;
}

/** Lo que el servidor manda a la interfaz de la persona con sesión (GET /api/yo). */
export const UsuarioSesion = Usuario.pick({ id: true, correo: true, nombre: true, rol: true, asesor_id_plantilla: true });
export type UsuarioSesion = z.infer<typeof UsuarioSesion>;

export const ACCIONES = [
  'ver_cotizaciones',
  'editar_cotizaciones',
  'capturar_costos',
  'editar_precios',
  'administrar_usuarios',
  'configurar_ia',
  'configurar_crm',
  /** Bitácora del sistema (altas, precios, llave, modelo y tope de la IA, entradas): GET /api/bitacora. Solo director. */
  'ver_bitacora',
  /** ADENDA A0b: aprobar (o negar) un precio con descuento mayor al tope o debajo del margen mínimo. Solo director. */
  'aprobar_precios',
  /** AUTORIZACIONES (Diego, 11-oct; "verde manda", 12-oct): Miguel y Juan Carlos (asesores internos) TAMBIÉN autorizan un
   *  DESCUENTO (más del tope o debajo del 30 % de margen; CODIGOS_QUE_AUTORIZAN_INTERNOS), nunca el suyo propio. Debajo
   *  del piso, del margen mínimo o con pérdida sigue siendo solo de Diego (`aprobar_precios`). Lo revisa el SERVIDOR con
   *  motivoNoAutoriza(). */
  'autorizar_descuentos',
  /** ADENDA A0b (11-oct, MANDA): "solo yo ver márgenes y utilidad esperada". Costo estimado, margen, utilidad, comisión
   *  y costos de la tabla: el SERVIDOR no los manda a nadie sin este permiso (vista.ts los quita de cada respuesta). */
  'ver_costos',
  /** Semáforo de margen (Diego, 11-oct, MANDA sobre lo anterior): Miguel y Juan Carlos (asesores internos) ven SOLO EL
   *  COLOR (verde ≥ 30 %, amarillo 20–30 %, rojo < 20 %) en cada opción y en el CRM, sin % ni montos. Desde el 13-oct
   *  (junta) también los externos y Optimus. El SERVIDOR manda a quien tiene este permiso (y no `ver_costos`) solo el color (vista.ts: `semaforo_margen`). */
  'ver_semaforo',
  /** PAPELERA (Diego, 13-oct): ver la papelera de cotizaciones, recuperar, y borrar CUALQUIER cotización (en cualquier
   *  estado). Solo director. Un vendedor borra solo las suyas en Borrador (motivoNoMandaAPapelera). */
  'administrar_papelera',
] as const;
export type Accion = (typeof ACCIONES)[number];

const PERMISOS: Readonly<Record<Rol, readonly Accion[]>> = Object.freeze({
  director: ACCIONES,
  asesor: ['ver_cotizaciones', 'editar_cotizaciones', 'capturar_costos', 'ver_semaforo', 'autorizar_descuentos'],
  // Diego, 13-oct (junta, MANDA sobre el 11-oct): TODOS ven el COLOR del semáforo, también externos y Optimus (nunca %, costos
  // ni montos: eso sigue siendo solo de `ver_costos`).
  externo: ['ver_cotizaciones', 'editar_cotizaciones', 'capturar_costos', 'ver_semaforo'],
  // ADENDA A0b: los técnicos NO entran al cotizador (el vendedor captura lo que reportan). Sin ningún permiso.
  tecnico: [],
});

/** AUTORIZACIONES (Diego, 11-oct) y tope de internos (Diego, 13-oct): ¿es Diego, Miguel o Juan Carlos? Director
 *  (aprobar_precios) o asesor interno (autorizar_descuentos), activo, y NUNCA un vendedor de Optimus (verificación
 *  adversarial del 13-oct: un asesor marcado de Optimus autorizaba descuentos ajenos). La usan motivoNoAutoriza (quién
 *  autoriza) y vendedorBajaComoInterno (quién baja hasta 10 % sin OK): una sola regla para los dos lados. */
export function puedeAutorizarDescuento(u: Pick<Usuario, 'rol' | 'activo'> & { optimus?: boolean | undefined }): boolean {
  return !u.optimus && (puede(u, 'aprobar_precios') || puede(u, 'autorizar_descuentos'));
}

/** ¿Puede este usuario hacer esto? Un usuario inactivo no puede nada. */
export function puede(usuario: Pick<Usuario, 'rol' | 'activo'>, accion: Accion): boolean {
  return usuario.activo && PERMISOS[usuario.rol].includes(accion);
}

/** «Hoy toca» (Diego, 2-nov-2026) es la lista de seguimiento de los asesores de Solarix (Juan Carlos, Miguel) y de Diego. Un vendedor externo no la
 *  tiene: su mensaje diría «de Solarix» y si es de Optimus le ofrecería la promo, que es decisión de Diego (verificación del 2-nov, ronda 2). */
export function usaHoyToca(usuario: Pick<Usuario, 'rol'> & { activo?: boolean }): boolean {
  // (la sesión de la web no trae `activo`: quien tiene sesión ya está activo; el servidor sí lo revisa)
  return puede({ rol: usuario.rol, activo: usuario.activo ?? true }, 'editar_cotizaciones') && usuario.rol !== 'externo';
}

/** ¿Puede entrar al cotizador (pedir enlace, tener sesión)? Activo y con un rol que entra (los técnicos no, A0b). */
export function puedeEntrar(usuario: Pick<Usuario, 'rol' | 'activo'>): boolean {
  return usuario.activo && (ROLES_ACTIVOS as readonly Rol[]).includes(usuario.rol);
}

/** Privacidad (ADENDA A0b, 10-oct, la hace cumplir el SERVIDOR): ¿puede ABRIR esta cotización (detalle, versiones,
 *  recibos, datos privados, PDF)? Director y asesores internos: todo. Un vendedor EXTERNO: solo las suyas (de las demás
 *  ve el renglón de la lista: cliente, monto, estado). No depende de la política de edición: es siempre así. */
export function puedeAbrirCotizacion(usuario: Pick<Usuario, 'id' | 'rol' | 'activo'>, cotizacion: { vendedor_id: string }): boolean {
  if (!puede(usuario, 'ver_cotizaciones')) return false;
  return usuario.rol !== 'externo' || cotizacion.vendedor_id === usuario.id;
}

// ───────────── Política por cotización: "vendedor externo edita solo lo suyo" (ENCENDIDA) ─────────────
// ADENDA A0b (10-oct): "los vendedores externos EDITAN solo las suyas; Diego, Miguel y Juan Carlos editan todo". Diego ya
// decidió: encendida por omisión, y el servidor no arranca con EXTERNO_SOLO_LO_SUYO=no. Se publica en
// ConfigPublica.politica_acceso. Ver ARQUITECTURA §12.10.

export const PoliticaAcceso = z.strictObject({
  /** true = un vendedor EXTERNO solo edita (borrador, CRM, archivos, IA, costos, versiones, PDF) las cotizaciones donde
   *  ÉL es el vendedor, y solo crea o reasigna cotizaciones a su propio nombre. VER sigue abierto: todos ven todo. */
  externo_edita_solo_lo_suyo: z.boolean(),
});
export type PoliticaAcceso = z.infer<typeof PoliticaAcceso>;

/** Lo que vale hoy: ENCENDIDA (ADENDA A0b). */
export const POLITICA_ACCESO_POR_OMISION: PoliticaAcceso = Object.freeze({ externo_edita_solo_lo_suyo: true });

/** Acciones que la política restringe por cotización (ver nunca se restringe). */
const ACCIONES_POR_COTIZACION: readonly Accion[] = ['editar_cotizaciones', 'capturar_costos'];

/** ¿Puede este usuario hacer `accion` sobre ESTA cotización? Primero `puede()` (rol y activo); luego la política.
 *  La política es obligatoria (sin valor por omisión): quien llama pasa la que está configurada, no una supuesta. */
export function puedeEnCotizacion(
  usuario: Pick<Usuario, 'id' | 'rol' | 'activo'>,
  accion: Accion,
  cotizacion: { vendedor_id: string },
  politica: PoliticaAcceso,
): boolean {
  if (!puede(usuario, accion)) return false;
  if (politica.externo_edita_solo_lo_suyo && usuario.rol === 'externo' && ACCIONES_POR_COTIZACION.includes(accion))
    return cotizacion.vendedor_id === usuario.id;
  return true;
}

/** ¿Puede dejar una cotización a nombre de `vendedorId` (al crearla o al cambiar de vendedor)?
 *  JUNTA, SEGUNDA TANDA (Diego, 13-oct, 17:30, MANDA): cotizar A NOMBRE DE OTRO vendedor (Juan Carlos le cotiza a Fernando
 *  Camou, que anda de viaje) solo lo hacen Diego, Miguel y Juan Carlos: los mismos que autorizan descuentos
 *  (puedeAutorizarDescuento; nunca un vendedor de Optimus). A su propio nombre, cualquiera que edite. Con la política
 *  apagada, como antes: cualquiera que edite. */
export function puedeAsignarVendedor(
  usuario: Pick<Usuario, 'id' | 'rol' | 'activo'> & { optimus?: boolean | undefined },
  vendedorId: string,
  politica: PoliticaAcceso,
): boolean {
  if (!puede(usuario, 'editar_cotizaciones')) return false;
  if (vendedorId === usuario.id || !politica.externo_edita_solo_lo_suyo) return true;
  return puedeAutorizarDescuento(usuario);
}

/** Cuerpo para dar de alta o editar un usuario (solo director). El rol "tecnico" ya no se da de alta (A0b). */
export const CuerpoUsuario = Usuario.pick({ correo: true, nombre: true, rol: true, activo: true, asesor_id_plantilla: true, whatsapp: true, comision_pct: true, optimus: true }).extend({
  rol: z.enum(ROLES_ACTIVOS, { error: () => 'Rol no permitido: director, asesor o externo (los técnicos no entran al cotizador; el vendedor captura sus costos).' }),
});
export type CuerpoUsuario = z.infer<typeof CuerpoUsuario>;

/** Una tarjeta de asesor tal como la trae la plantilla del PDF (assets/asesores/asesores.json, solo las activas). */
export interface FichaAsesorPlantilla {
  id: string;
  nombre_en_pdf: string | null;
  correo: string | null;
}
export type ViaTarjetaAsesor = 'id' | 'correo' | 'nombre';

const normalizarNombreAsesor = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

/** Diego, junta del 13-oct: dio de alta a Miguel Cota con la tarjeta «miguel-cota» y la plantilla lo conoce como «miguel»;
 *  su PDF no salía. La tarjeta se busca así: por id (asesor_id_plantilla); si la plantilla no conoce ese id, por CORREO
 *  (el del usuario contra el de la ficha); si tampoco, por nombre sin acentos ni mayúsculas (el del usuario o su id con
 *  espacios: «miguel-cota» → «miguel cota»). Solo cuenta si cuadra con UNA sola ficha. null = sin tarjeta: el PDF sale
 *  con la tarjeta de respaldo (su nombre y su WhatsApp, sin foto). Sin fichas (puente de antes): solo por id. */
export function resolverTarjetaAsesor(
  vendedor: Pick<Usuario, 'asesor_id_plantilla' | 'correo' | 'nombre'>,
  asesores: readonly string[],
  fichas: readonly FichaAsesorPlantilla[] = [],
): { id: string; via: ViaTarjetaAsesor } | null {
  const id = vendedor.asesor_id_plantilla;
  if (id && asesores.includes(id)) return { id, via: 'id' };
  const activas = fichas.filter((f) => asesores.includes(f.id));
  const unica = (xs: FichaAsesorPlantilla[]) => (xs.length === 1 ? xs[0]! : null);
  const correo = vendedor.correo.trim().toLowerCase();
  const porCorreo = correo ? unica(activas.filter((f) => (f.correo ?? '').trim().toLowerCase() === correo)) : null;
  if (porCorreo) return { id: porCorreo.id, via: 'correo' };
  const nombres = [vendedor.nombre, id ? id.replace(/-/g, ' ') : ''].map(normalizarNombreAsesor).filter(Boolean);
  const porNombre = unica(activas.filter((f) => f.nombre_en_pdf && nombres.includes(normalizarNombreAsesor(f.nombre_en_pdf))));
  if (porNombre) return { id: porNombre.id, via: 'nombre' };
  return null;
}
